import { useEffect } from "react";
import { Navigate, NavLink, Route, Routes } from "react-router-dom";
import { CalendarCheck, CalendarDays, LayoutDashboard, Gift, Users, BookOpen, ShoppingBag, LogOut, ExternalLink } from "lucide-react";
import { useAdminAuth } from "../../lib/admin";
import { Loading } from "../../components/admin/ui";
import { AppointmentsAdmin } from "../../components/admin/AppointmentsAdmin";
import { GiftCardsAdmin } from "../../components/admin/GiftCardsAdmin";
import { ReferralsAdmin } from "../../components/admin/ReferralsAdmin";
import { PostsAdmin } from "../../components/admin/PostsAdmin";
import { ProductsAdmin } from "../../components/admin/ProductsAdmin";
import { SummaryAdmin } from "../../components/admin/SummaryAdmin";
import { CalendarAdmin } from "../../components/admin/CalendarAdmin";

const TABS = [
  ["riepilogo", "Riepilogo", LayoutDashboard, SummaryAdmin],
  ["calendario", "Calendario", CalendarDays, CalendarAdmin],
  ["prenotazioni", "Prenotazioni", CalendarCheck, AppointmentsAdmin],
  ["buoni", "Buoni regalo", Gift, GiftCardsAdmin],
  ["amiche", "Porta un'amica", Users, ReferralsAdmin],
  ["blog", "Blog", BookOpen, PostsAdmin],
  ["shop", "Shop", ShoppingBag, ProductsAdmin],
];

const Tabs = () => (
  <nav className="flex gap-1 overflow-x-auto -mx-5 px-5 sm:mx-0 sm:px-0 scrollbar-none" data-testid="admin-tabs">
    {TABS.map(([path, label, Icon]) => (
      <NavLink key={path} to={`/admin/${path}`} data-testid={`admin-tab-${path}`}
        className={({ isActive }) => `shrink-0 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors duration-200 ${isActive ? "bg-[#14110F] text-[#F6F2EC]" : "text-[#4f4841] hover:bg-[#efe9df]"}`}>
        <Icon className="w-4 h-4" /> {label}
      </NavLink>
    ))}
  </nav>
);

export default function AdminDashboard() {
  const { user, logout } = useAdminAuth();
  useEffect(() => { document.title = "Area riservata · Gioia"; }, []);
  if (user === null) return <div className="min-h-screen bg-[#F6F2EC]"><Loading /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return (
    <div className="min-h-screen bg-[#F6F2EC] text-[#14110F]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-[#F6F2EC]/90 backdrop-blur-md border-b border-[#e7dfd3]">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/images/logo-gioia-dark.png" alt="GIOIA" className="h-10 w-auto" />
            <span className="hidden sm:inline text-[0.68rem] tracking-[0.22em] uppercase text-[#8A6A3A]">Area riservata</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-3">
            <span className="hidden md:inline text-sm text-[#6b625a]" data-testid="admin-user-email">{user.email}</span>
            <a href="/" target="_blank" rel="noreferrer" className="p-2 text-[#6b625a] hover:text-[#8A6A3A]" aria-label="Apri il sito" data-testid="admin-open-site"><ExternalLink className="w-4 h-4" /></a>
            <button type="button" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-[#ddd4c7] px-3 py-1.5 text-[0.7rem] tracking-[0.14em] uppercase hover:border-[#8A6A3A]" data-testid="admin-logout-btn">
              <LogOut className="w-3.5 h-3.5" /> Esci
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-5 pb-3"><Tabs /></div>
      </header>
      <main className="max-w-6xl mx-auto px-5 py-8 sm:py-10">
        <Routes>
          {TABS.map(([path, label, , Comp]) => (
            <Route key={path} path={path} element={<><h1 className="font-serif-d font-light text-4xl sm:text-5xl mb-8">{label}</h1><Comp /></>} />
          ))}
          <Route path="*" element={<Navigate to="/admin/riepilogo" replace />} />
        </Routes>
      </main>
    </div>
  );
}
